import { create } from 'zustand';
import { useEffect } from 'react';
import { api } from '@/lib/api';
import { useSync } from '@/sync/status';
import { useSession } from '@/stores/session';

export interface NotifItem { id: string; event: string; title: string; body: string; data?: Record<string, unknown> | null; read: boolean; at: string }
interface N { unread: number; items: NotifItem[]; loading: boolean; error: string | null; refresh: (full?: boolean) => Promise<void>; markAll: () => Promise<void>; markOne: (id: string) => Promise<void> }

export const useNotifs = create<N>((set, get) => ({
  unread: 0, items: [], loading: false, error: null,
  async refresh(full = false) {
    try {
      set({ loading: full, error: null });
      const r = await api.get<{ unread: number; items: NotifItem[] }>('/notifications', { query: { limit: full ? 50 : 1 } });
      set(full ? { unread: r.unread, items: r.items, loading: false } : { unread: r.unread, loading: false });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },
  async markAll() {
    set({ unread: 0, items: get().items.map((i) => ({ ...i, read: true })) });
    try { await api.post('/notifications/read', { all: true }); } catch { /* retried on next open */ }
  },
  async markOne(id) {
    set({ items: get().items.map((i) => (i.id === id ? { ...i, read: true } : i)), unread: Math.max(0, get().unread - 1) });
    try { await api.post('/notifications/read', { ids: [id] }); } catch { /* ignore */ }
  },
}));

export function useUnread(): number {
  const online = useSync((s) => s.online);
  const phase = useSession((s) => s.phase);
  const unread = useNotifs((s) => s.unread);
  const refresh = useNotifs((s) => s.refresh);
  useEffect(() => {
    if (!online || phase !== 'ready') return;
    void refresh();
    const t = setInterval(() => void refresh(), 90_000);
    return () => clearInterval(t);
  }, [online, phase, refresh]);
  return unread;
}
